import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { fetchCasaMapaPartnerListingComps } from "../../services/casamapaPartnerAgencyApi";
import { useCurrency, useLocale } from "../../intl";
import "../../styles/partnerPortalDashboard.css";
import "../../styles/partnerListingsDashboard.css";
import "../../styles/partnerCasaMapaComps.css";

const COPY = "realEstate.casaMapa.admin.partnerComps";

function CompCard({ listing, formatMoney, t, highlight = false }) {
  const location = [listing.neighborhood, listing.municipality].filter(Boolean).join(", ") || "—";
  return (
    <article className={`cm-partner-comps__card${highlight ? " cm-partner-comps__card--subject" : ""}`}>
      {listing.photoUrl ? (
        <img src={listing.photoUrl} alt="" className="cm-partner-comps__photo" />
      ) : (
        <span className="cm-partner-comps__photo cm-partner-comps__photo--empty" aria-hidden="true">
          ⌂
        </span>
      )}
      <div className="cm-partner-comps__card-body">
        {highlight ? <span className="cm-partner-comps__badge">{t(`${COPY}.yourListing`)}</span> : null}
        <h3>{listing.title}</h3>
        <p className="muted">{location}</p>
        <p className="cm-partner-comps__price">
          {listing.priceCop != null ? formatMoney(listing.priceCop) : "—"}
        </p>
        <dl className="cm-partner-comps__facts">
          <div>
            <dt>{t(`${COPY}.facts.area`)}</dt>
            <dd>{listing.areaSqm != null ? `${listing.areaSqm} m²` : "—"}</dd>
          </div>
          <div>
            <dt>{t(`${COPY}.facts.ppsm`)}</dt>
            <dd>{listing.pricePerM2Cop != null ? formatMoney(listing.pricePerM2Cop) : "—"}</dd>
          </div>
          <div>
            <dt>{t(`${COPY}.facts.bedsBaths`)}</dt>
            <dd>
              {listing.bedrooms ?? "—"} / {listing.bathrooms ?? "—"}
            </dd>
          </div>
          <div>
            <dt>{t(`${COPY}.facts.dom`)}</dt>
            <dd>{listing.daysOnMarket != null ? listing.daysOnMarket : "—"}</dd>
          </div>
        </dl>
        {listing.publicUrl ? (
          <a className="partner-dashboard-btn" href={listing.publicUrl} target="_blank" rel="noreferrer">
            {t(`${COPY}.viewPublic`)}
          </a>
        ) : null}
      </div>
    </article>
  );
}

export default function PartnerCasaMapaCompsPage() {
  const { listingId } = useParams();
  const { path } = usePartnerPortalBase();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaPartnerListingComps(listingId);
      setPayload(result);
    } catch (err) {
      setPayload(null);
      setError(err?.message || t(`${COPY}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [listingId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const pricing = payload?.pricingSignals;

  return (
    <div className="cm-partner-comps">
      <header className="cm-partner-comps__hero partner-dashboard-hero">
        <div>
          <p className="cm-partner-comps__eyebrow">{t(`${COPY}.eyebrow`)}</p>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="partner-dashboard-hero__intro">{t(`${COPY}.subtitle`)}</p>
        </div>
        <div className="partner-dashboard-hero__actions">
          <Link className="partner-dashboard-btn" to={path(`listings/${listingId}`)}>
            {t(`${COPY}.backToHealth`)}
          </Link>
          <button type="button" className="partner-dashboard-btn" onClick={() => window.print()}>
            {t(`${COPY}.print`)}
          </button>
          <button type="button" className="partner-dashboard-btn partner-dashboard-btn--primary" onClick={load}>
            {t(`${COPY}.refresh`)}
          </button>
        </div>
      </header>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {loading ? <p className="muted">{t(`${COPY}.loading`)}</p> : null}

      {!loading && payload?.subject ? (
        <>
          <section className="partner-panel cm-partner-comps__signal">
            <h2>{t(`${COPY}.pricingTitle`)}</h2>
            {pricing?.available ? (
              <>
                <p className={`cm-partner-comps__band cm-partner-comps__band--${pricing.band}`}>
                  {t(`realEstate.casaMapa.admin.partnerListings.pricing.bands.${pricing.band}`)}
                </p>
                {pricing.relativePositionPercent != null ? (
                  <p>
                    {t(`realEstate.casaMapa.admin.partnerListings.pricing.vsMedian`, {
                      percent:
                        pricing.relativePositionPercent > 0
                          ? `+${pricing.relativePositionPercent}`
                          : String(pricing.relativePositionPercent),
                    })}
                  </p>
                ) : null}
              </>
            ) : (
              <p className="muted">{t(`realEstate.casaMapa.admin.partnerListings.pricing.unavailable`)}</p>
            )}
          </section>

          <section className="cm-partner-comps__grid" aria-label={t(`${COPY}.gridLabel`)}>
            <CompCard listing={payload.subject} formatMoney={formatMoney} t={t} highlight />
            {(payload.comps || []).map((listing) => (
              <CompCard key={listing.id} listing={listing} formatMoney={formatMoney} t={t} />
            ))}
          </section>

          {!payload.comps?.length ? (
            <p className="muted">{t(`${COPY}.emptyComps`)}</p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
